import type {
  Found,
  HeldQueryResult,
  InsightsFailure,
  InsightsQueryResult,
} from "src/pages/ad-studio/insights/performance/types";

export function mapQueryResult<T, U, Failure>(
  result: InsightsQueryResult<T, Failure>,
  to: (value: T) => U,
): InsightsQueryResult<U, Failure> {
  switch (result.status) {
    case "loading":
    case "failed": {
      return result;
    }
    case "loaded": {
      return { status: "loaded", value: to(result.value) };
    }
    default: {
      return result satisfies never;
    }
  }
}

export function mapHeldResult<T, U, Failure>(
  result: HeldQueryResult<T, Failure>,
  to: (value: T) => U,
): HeldQueryResult<U, Failure> {
  switch (result.status) {
    case "reloading": {
      return { status: "reloading", value: to(result.value) };
    }
    case "loading":
    case "failed":
    case "loaded": {
      return mapQueryResult(result, to);
    }
    default: {
      return result satisfies never;
    }
  }
}

export const mapFound =
  <T, U>(to: (value: T) => U) =>
  (found: Found<T>): Found<U> => {
    switch (found.kind) {
      case "found": {
        return { kind: "found", value: to(found.value) };
      }
      case "not-found": {
        return found;
      }
      default: {
        return found satisfies never;
      }
    }
  };

export function loadedValueOf<T, Failure>(
  result: InsightsQueryResult<T, Failure>,
): T | "not-loaded" {
  switch (result.status) {
    case "loading":
    case "failed": {
      return "not-loaded";
    }
    case "loaded": {
      return result.value;
    }
    default: {
      return result satisfies never;
    }
  }
}

export function failureMessage(failure: InsightsFailure): string {
  switch (failure.cause) {
    case "network": {
      return "Couldn't reach Hightouch. Check your connection and try again.";
    }
    case "not-built": {
      return "Insights is still preparing this workspace's data. Check back in a few minutes.";
    }
    case "rejected": {
      return "Insights couldn't run this query. Contact support if it keeps happening.";
    }
    case "bad-response": {
      return "Something went wrong on our end. Try again, or contact support if it keeps happening.";
    }
    case "invalid-query": {
      return "We couldn't build this request. Try again, or contact support if it keeps happening.";
    }
    case "invalid-attributes": {
      return "This embed is missing its ad platform or date range. Try again, or contact support if it keeps happening.";
    }
    case "sync-not-connected": {
      return "This ad platform isn't connected anymore. Reconnect it to see its performance.";
    }
    case "filter-not-available": {
      return "This table's filters don't apply at this level. Ask for it again without them, or at a level they apply to.";
    }
    case "no-creative-tags": {
      return "This workspace has no creative tags to filter by. Ask for these ads again without a tag filter.";
    }
    case "group-not-available": {
      return "This grouping isn't available anymore. Group the list again and reopen it.";
    }
    case "unknown-metrics": {
      return `This workspace doesn't report ${failure.names.join(", ")}. Ask for it again with metrics this workspace reports.`;
    }
    default: {
      return failure satisfies never;
    }
  }
}
